/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import IsoModal from './IsoModal';
import {useSidebar} from "../../../../context/CollapsedContext"

// Memoized checkbox component
const TA_VARIANT_OPTIONS = [
  { value: '5FT', label: '5FT' },
  { value: '10FT', label: '10FT' },
  { value: '15', label: '15' },
  { value: '20', label: '20' },
  // { value: 'SPECIAL', label: 'Special' }
];

// Define height mappings for different TA variants
const TA_HEIGHTS = { '5FT': 30, '10FT': 60, '15': 90, '20': 120, 'SPECIAL': 72 };
const TA_HEIGHTS_PDF = { '5FT': 15, '10FT': 30, '15': 45, '20': 60, 'SPECIAL': 65 };

// STK heights matched to TA heights
const STK_HEIGHTS = { '5FT': 30, '10FT': 60, '15FT': 90, '20FT': 120 };
const STK_HEIGHTS_PDF = { '5FT': 15, '10FT': 30, '15FT': 45, '20FT': 60 };

const CheckboxLabel = React.memo(({
  label,
  portionIndex,
  isChecked,
  onChange,
  boxName,
  handleSTKChange,
  stkVariant,
  handleTAChange,
  taVariant,
  motorSelections,
  onMotorSelect,
  is5Ft
}) => {
  const dropdownPadClass = is5Ft ? 'pl-0' : 'pl-[5px]';
  
  const handleChange = useCallback(() => {
    onChange(boxName, portionIndex, label);

    // If unchecking PW, reset motor selection for this portion
    if (label === 'PW' && isChecked && motorSelections[boxName] === portionIndex) {
      onMotorSelect(boxName, portionIndex, '');
    }

  }, [boxName, portionIndex, label, onChange, isChecked, motorSelections, onMotorSelect]);

  const handleSTKSelectChange = useCallback((e) => {
    handleSTKChange(boxName, portionIndex, e);
  }, [boxName, portionIndex, handleSTKChange]);

  const handleTASelectChange = useCallback((e) => {
    handleTAChange(boxName, portionIndex, e);
  }, [boxName, portionIndex, handleTAChange]);

  return (
    <label className="flex flex-col items-start cursor-pointer -mt-8">
      <div className="flex items-start space-x-1">
        <div className="relative">
          <input
            type="checkbox"
            className="w-3.5 h-3.5 border border-red-600 rounded appearance-none checked:bg-red-600 bg-white cursor-pointer"
            checked={isChecked}
            onChange={handleChange}
          />
          {isChecked && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        <span className="text-[10px] leading-none mt-0.5">{label}</span>
      </div>
      {label === 'STK' && isChecked && (
        <div className={`mt-0.5 ${dropdownPadClass}`}>
          <select
            className="w-11 text-[8px] leading-none font-medium border border-red-600 rounded px-1 py-0.5 bg-white shadow-sm"
            value={stkVariant || '5FT'}
            onChange={handleSTKSelectChange}
          >
            <option value="5FT">5FT</option>
            <option value="10FT">10FT</option>
            <option value="15FT">15FT</option>
            <option value="20FT">20FT</option>
          </select>
        </div>
      )}
      {label === 'TA' && isChecked && (
        <div className={`mt-0.5 ${dropdownPadClass}`}>
          <select
            className="w-11 text-[8px] leading-none font-medium border border-red-600 rounded px-1 py-0.5 bg-white shadow-sm"
            value={taVariant || '5FT'}
            onChange={handleTASelectChange}
          >
            {TA_VARIANT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
    </label>
  );
}, (prevProps, nextProps) => {
  return prevProps.isChecked === nextProps.isChecked && prevProps.stkVariant === nextProps.stkVariant && prevProps.taVariant === nextProps.taVariant && prevProps.motorSelections === nextProps.motorSelections && prevProps.is5Ft === nextProps.is5Ft;
});

const isFiveFtModule = (alt) => { return alt && (alt === 'LFT' || alt === 'LTOPTION' || alt === '10FT'); };

const isFiveFtModuleOnly = (alt) => { return alt && alt.includes('5FT'); };

// Memoized BoxItem component
const BoxItem = React.memo(({
  item,
  portionIndex,
  boxName,
  isFlippedVertically,
  visibleLabels,
  checkedItems,
  handleCheckboxChange,
  imageUrl,
  onImageRightClick,
  hasMotor,
  motorImageUrls,
  hasTA,
  taImageUrls,
  hasSTK,
  stkImageUrls,
  isFlipped,
  onMotorSelect,
  motorVariantByBox,
  onDeletePortion,
  collapsed,
  boxLabel,
  conveyorStyle,
  isGeneratingPdf,
  handleSTKChange,
  stkVariantByPortion,
  handleTAChange,
  taVariantByPortion,
  motorSelections
}) => {
  // Get the correct motor image based on selection
  const motorVariant = motorVariantByBox?.[boxName] || 'motor';
  const motorImageUrl = motorImageUrls?.[boxName]?.[motorVariant] || null;
  
  // Get the selected STK variant for this portion (default to 5FT)
  const stkVariant = stkVariantByPortion?.[boxName]?.[portionIndex] || '5FT';
  // Get the selected TA variant for this portion (default to 5FT)
  const taVariant = taVariantByPortion?.[boxName]?.[portionIndex] || '5FT';
  
  // Always use the 5FT TA image and scale vertically for 10/15/20 variants
  const taSlot = taImageUrls?.[boxName];
  const taImageUrl = isFlipped ? taSlot?.down : taSlot?.up;
  
  // Always use the 5FT STK image and scale vertically for 10/15/20FT variants
  const stkImageUrl = isFlipped
    ? stkImageUrls?.[boxName]?.low
    : stkImageUrls?.[boxName]?.high;
  
// Get vertical position based on TA variant (flip-aware)
  const getTAVerticalPosition = useCallback(() => {
    if (isFlippedVertically) {
      // Flipped: mirror positions downward
      if (taVariant === '5FT') return { top: '51.2%', transform: 'translateY(-50%)' };
      if (taVariant === '10FT') return { top: '54%', transform: 'translateY(-55%)' };
      if (taVariant === '15') return { top: '57%', transform: 'translateY(-60%)' };
      if (taVariant === '20') return { top: '60%', transform: 'translateY(-65%)' };
      return { top: '50%', transform: 'translateY(-50%)' };
    }
    if (taVariant === '5FT') return { top: '48.8%', transform: 'translateY(-50%)' };
    if (taVariant === '10FT') return { top: '46%', transform: 'translateY(-45%)' };
    if (taVariant === '15') return { top: '43%', transform: 'translateY(-40%)' };
    if (taVariant === '20') return { top: '40%', transform: 'translateY(-35%)' };
    return { top: '50%', transform: 'translateY(-50%)' }; // default
  }, [taVariant, isFlippedVertically]);

  // Get vertical position for PDF based on TA variant (flip-aware)
  const getTAVerticalPositionPDF = useCallback(() => {
    if (isFlippedVertically) {
      if (taVariant === '5FT') return { top: '50%', transform: 'translateY(-55%)' };
      if (taVariant === '10FT') return { top: '54%', transform: 'translateY(-60%)' };
      if (taVariant === '15') return { top: '58%', transform: 'translateY(-65%)' };
      if (taVariant === '20') return { top: '62%', transform: 'translateY(-70%)' };
      return { top: '50%', transform: 'translateY(-55%)' };
    }
    if (taVariant === '5FT') return { top: '50%', transform: 'translateY(-45%)' };
    if (taVariant === '10FT') return { top: '46%', transform: 'translateY(-40%)' };
    if (taVariant === '15') return { top: '42%', transform: 'translateY(-35%)' };
    if (taVariant === '20') return { top: '38%', transform: 'translateY(-30%)' };
    return { top: '50%', transform: 'translateY(-45%)' }; // default
  }, [taVariant, isFlippedVertically]);

const is5Ft = isFiveFtModuleOnly(item.alt);

const checkboxLabels = useMemo(() => {
  return visibleLabels.map(label => {
    const key = `${portionIndex}-${label}`;
    const isChecked = checkedItems.get(key) || false;

    if (label === 'MT') {
      // Check if this is an Extreme duty conveyor
      const styleValue = typeof conveyorStyle === 'string'
        ? conveyorStyle
        : conveyorStyle?.value || '';
      const styleUpper = styleValue.toUpperCase();
      const isExtremeDuty = styleUpper.includes('EX') || styleUpper.includes('EXTREME');

      if (isExtremeDuty) {
        // Extreme duty: keep dropdown for 7.5hp selection
        const currentVariant = hasMotor ? (motorVariantByBox?.[boxName] || '') : '';
        return (
          <div key={key} className="w-full">
            <select
              className={`w-full text-[8px] leading-none border border-red-600 rounded px-1 py-0.5 bg-white`}
              value={currentVariant}
              onChange={(e) => onMotorSelect(boxName, portionIndex, e.target.value)}
            >
              <option value="">Select MT</option>
              <option value="motor2">7.5hp</option>
            </select>
          </div>
        );
      }

      // Heavy duty: MT is a checkbox that auto-selects 5hp motor
      return (
        <CheckboxLabel key={key} label={label} portionIndex={portionIndex} isChecked={isChecked} onChange={handleCheckboxChange} boxName={boxName} handleSTKChange={handleSTKChange} stkVariant={stkVariant} handleTAChange={handleTAChange} taVariant={taVariant} motorSelections={motorSelections} onMotorSelect={onMotorSelect} is5Ft={is5Ft} />
      );

      /* COMMENTED OUT: Previous dropdown for Heavy Duty 3hp/5hp selection
      const currentVariant = hasMotor ? (motorVariantByBox?.[boxName] || '') : '';
      const styleHasPW = item.labels?.includes('PW');
      const isPWSelected = checkedItems.get(`${portionIndex}-PW`) === true;
      const isDisabled = styleHasPW ? !isPWSelected : false;

      return (
        <div key={key} className="w-full">
          <select
            className={`w-full text-[8px] border border-red-600 rounded px-1 py-0.5 ${isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'}`}
            value={currentVariant}
            onChange={(e) => onMotorSelect(boxName, portionIndex, e.target.value)}
            disabled={isDisabled}
            title={isDisabled ? 'Select PW to enable motor options' : ''}
          >
            <option value="">Select MT</option>
            <option value="motor">5hp</option>
            <option value="motor1">3hp</option>
          </select>
        </div>
      );
      */
    }

    return (
      <CheckboxLabel key={key} label={label} portionIndex={portionIndex} isChecked={isChecked} onChange={handleCheckboxChange} boxName={boxName} handleSTKChange={handleSTKChange} stkVariant={stkVariant} handleTAChange={handleTAChange} taVariant={taVariant} motorSelections={motorSelections} onMotorSelect={onMotorSelect} is5Ft={is5Ft} />
    );
  });
}, [visibleLabels, portionIndex, checkedItems, handleCheckboxChange, boxName, onMotorSelect, motorVariantByBox, hasMotor, item.labels, conveyorStyle, handleSTKChange, stkVariant, handleTAChange, taVariant, is5Ft]);

  return (
    <div
      className={`h-full flex-shrink-0 flex flex-col items-center p-0 m-0 relative snap-start ${
        item.alt.startsWith("SP-") ? "" : isFiveFtModuleOnly(item.alt) ? "w-[40px]" : "w-[80px]"
      }`}
      style={{ width: item.alt.startsWith("SP-") ? `${item.width * 8}px` : isFiveFtModuleOnly(item.alt) ? "40px" : "80px", }}
        id={`${boxName}-module-${portionIndex}`}
    >
      {item?.type === "option" && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: '100%', justifyContent: 'center', position: 'relative' }}>
          {!isFlippedVertically && checkboxLabels.length > 0 && !isGeneratingPdf && (
            <div className="absolute top-10 left-0 right-0" style={{ width: isFiveFtModuleOnly(item.alt) ? '40px' : '100%', overflow: 'visible' }}>
              <div className={`flex flex-col items-start text-xs space-y-1 mb-2 ${isFiveFtModuleOnly(item.alt) ? 'px-0' : 'px-2'}`}>
                {checkboxLabels}
              </div>
            </div>
          )}

          {/* Box label (IF #1 / OF #1) — bottom when normal, top when flipped */}
          {imageUrl && boxLabel && !isGeneratingPdf && (
            <div style={{ position: 'absolute', [isFlippedVertically ? 'top' : 'bottom']: '4px', left: 0, right: 0, pointerEvents: 'none', zIndex: 10 }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'black', textAlign: 'center' }}>
                {boxLabel}
              </div>
            </div>
          )}

          {/* Box label for PDF — centered */}
          {imageUrl && boxLabel && isGeneratingPdf && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10 }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'black', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                {boxLabel}
              </div>
            </div>
          )}

          {imageUrl && (
            <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', transform: isGeneratingPdf ? 'translateY(18px)' : (isFlippedVertically ? 'translateY(-120px)' : 'translateY(120px)'), pointerEvents: 'none' }} >
              {/* Main image with conditional styling.
                  Non-5FT, non-spacer, non-ATBL modules use a CSS scaleY(-1)
                  flip when the box is flipped — backend filename stays
                  identical so line-item metadata is unaffected. 5FT uses a
                  backend variant swap (handled in generateConveyorFilename),
                  spacers/ATBL keep their natural orientation. */}
              <div style={isFiveFtModuleOnly(item.alt)
                ? { position: 'relative', display: 'flex', justifyContent: 'center', width: '100%' }
                : { position: 'relative', display: 'inline-flex' }}>
              <img
                src={imageUrl}
                alt={item.alt}
                style={
                  isGeneratingPdf
                    ? {
                        objectFit: (isFiveFtModule(item.alt) || isFiveFtModuleOnly(item.alt)) ? 'fill' : 'contain',
                        objectPosition: 'center',
                        backgroundColor: '#fff',
                        height: '40px',
                        width: isFiveFtModuleOnly(item.alt) ? '35px' : '70px',
                        display: 'block',
                        flex: '0 0 auto',
                        maxWidth: '100%',
                        minWidth: '20px',
                        imageRendering: 'crisp-edges',
                        margin: '0 auto',
                        transform: (isFlippedVertically && item.alt && !item.alt.startsWith('SP-') && !isFiveFtModuleOnly(item.alt) && item.alt !== 'ATBL') ? 'scaleY(-1)' : undefined
                      }
                    : {
                        objectFit: item.alt.startsWith('SP-') ? 'contain' : (isFiveFtModule(item.alt) || isFiveFtModuleOnly(item.alt)) ? 'fill' : 'contain',
                        transition: 'width 0.3s ease',
                        objectPosition: 'center',
                        backgroundColor: '#fff',
                        height: item.alt.startsWith('SP-') ? '9.5%' : ((isFiveFtModuleOnly(item.alt) || isFiveFtModule(item.alt)) ? '9.5%' : 'auto'),
                        display: 'block',
                        flex: '0 0 auto',
                        width: (isFiveFtModuleOnly(item.alt) || isFiveFtModule(item.alt)) ? '100%' : 'auto',
                        maxWidth: '100%',
                        minWidth: '20px',
                        imageRendering: 'crisp-edges',
                        margin: '0 auto',
                        pointerEvents: 'auto',
                        transform: (isFlippedVertically && item.alt && !item.alt.startsWith('SP-') && !isFiveFtModuleOnly(item.alt) && item.alt !== 'ATBL') ? 'scaleY(-1)' : undefined
                      }
                }
                loading="lazy"
                onError={(e) => {
                  console.error('❌ Image failed to load:', portionIndex, item.alt);
                  if (imageUrl) {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRkZGRkZGIi8+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5Ij5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4=';
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
                onLoad={() => { console.log('✅ Image loaded successfully:', portionIndex, item.alt); }}
                onContextMenu={(e) => onImageRightClick(e, item, portionIndex)}
              />

              {/* Motor image — anchored INSIDE the conveyor image itself (its container
                  hugs the image), so it always sits on top of whichever conveyor is
                  chosen, regardless of module height or flip.
                  Side follows the saw: the motor mounts on the end nearest the center
                  saw, so the box left of the saw anchors right and the box right of the
                  saw anchors left. This keys off boxName (the physical side, which never
                  swaps) — NOT the yellow/gray infeed-outfeed color, which flips with
                  isRotatedBox while the sides stay put. */}
              {hasMotor && motorImageUrl && (
                <div style={{ position: 'absolute', top: '2px', [boxName === 'left' ? 'right' : 'left']: '4px', zIndex: 10 }}>
                  <img
                    src={motorImageUrl}
                    alt="Motor"
                    style={
                      isGeneratingPdf
                        ? {
                            width: isFiveFtModuleOnly(item.alt) ? '0.6rem' : '0.75rem',
                            height: isFiveFtModuleOnly(item.alt) ? '0.6rem' : '0.75rem',
                            objectFit: 'contain',
                            imageRendering: 'crisp-edges'
                          }
                        : {
                            width: isFiveFtModuleOnly(item.alt) ? '1.1rem' : '1.5rem',
                            height: isFiveFtModuleOnly(item.alt) ? '1.1rem' : '1.5rem',
                            objectFit: 'contain',
                            imageRendering: 'crisp-edges'
                          }
                    }
                  />
                </div>
              )}
              </div>

              {/* Spacer feet label overlay */}
              {item.alt.startsWith('SP-') && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}>
                  <span style={{
                    fontSize: isGeneratingPdf ? '10px' : '12px',
                    fontWeight: 'bold',
                    color: '#000',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    whiteSpace: 'nowrap',
                  }}>
                    {item.width} FT
                  </span>
                </div>
              )}

              {/* Delete button */}
              {!isGeneratingPdf && (
                <button
                  type="button"
                  style={{ pointerEvents: 'auto' }}
                  className="absolute top-40 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 z-10"
                  title="Remove this module"
                  onClick={(e) => { e.stopPropagation(); onDeletePortion && onDeletePortion(boxName, portionIndex); }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"> <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> </svg>
                </button>
              )}

              {/* TA image (left/right) with conditional styling */}
              {hasTA && taImageUrl && (
                <div
                  style={
                    isGeneratingPdf
                      ? {
                          position: 'absolute',
                          [boxName === 'left' ? 'left' : 'right']: '-8px',
                          ...getTAVerticalPositionPDF(),
                          zIndex: 10
                        }
                      : {
                          position: 'absolute',
                          [boxName === 'left' ? 'left' : 'right']: '-8px',
                          ...getTAVerticalPosition(),
                          zIndex: 10
                        }
                  }
                >
                  <img
                    src={taImageUrl}
                    alt="TA component"
                    style={
                      isGeneratingPdf
                        ? {
                            width: '8px',
                            height: `${TA_HEIGHTS_PDF[taVariant] || TA_HEIGHTS_PDF['5FT']}px`,
                            imageRendering: 'crisp-edges'
                          }
                        : {
                            width: '8px',
                            height: `${TA_HEIGHTS[taVariant] || TA_HEIGHTS['5FT']}px`,
                            imageRendering: 'crisp-edges'
                          }
                    }
                    onError={(e) => {
                      console.error('TA image failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* STK image (center) with conditional styling */}
              {hasSTK && stkImageUrl && (
                <div
                  style={
                    isGeneratingPdf
                      ? {
                          position: 'absolute',
                          left: '70%',
                          transform: 'translateX(-50%)',
                          zIndex: 10,
                          top: isFlippedVertically
                            ? 'calc(50% + 15px)'
                            : `calc(50% - ${stkVariant === '20FT' ? '83px' : stkVariant === '15FT' ? '68px' : stkVariant === '10FT' ? '53px' : '38px'})`
                        }
                      : {
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 10,
                          top: isFlippedVertically
                            ? 'calc(50% + 19px)'
                            : `calc(50% - ${stkVariant === '20FT' ? '139px' : stkVariant === '15FT' ? '109px' : stkVariant === '10FT' ? '79px' : '49px'})`
                        }
                  }
                >
                  <img
                    src={stkImageUrl}
                    alt="STK component"
                    style={
                      isGeneratingPdf
                        ? {
                            width: '8px',
                            height: `${STK_HEIGHTS_PDF[stkVariant] || STK_HEIGHTS_PDF['5FT']}px`,
                            imageRendering: 'crisp-edges'
                          }
                        : {
                            width: '8px',
                            height: `${STK_HEIGHTS[stkVariant] || STK_HEIGHTS['5FT']}px`,
                            imageRendering: 'crisp-edges'
                          }
                    }
                    onError={(e) => {
                      console.error('STK image failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {isFlippedVertically && checkboxLabels.length > 0 && !isGeneratingPdf && (
            <div className="absolute bottom-1 left-0 right-0" style={{ width: isFiveFtModuleOnly(item.alt) ? '40px' : '100%', overflow: 'visible' }}>
              <div className={`flex flex-col items-start text-xs space-y-1 ${isFiveFtModuleOnly(item.alt) ? 'px-0' : 'px-2'}`}>
                {checkboxLabels}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const BoxComponent = ({
  boxItems,
  boxName,
  isRotatedBox,
  setIsRotatedBox,
  checkedItems,
  handleCheckboxChange,
  conveyorStyle,
  isReversed,
  generateConveyorFilename,
  isFlippedVertically,
  onBoxItemsChange,
  onCheckedItemsChange,
  setActiveBox,
  motorSelections,
  taSelections,
  stkSelections,
  filenameFlipStates,
  globalImageCache,
  setGlobalImageCache,
  cacheClearTrigger,
  imageDetails,
  imageDisplayBox,
  isImageBox,
  getImageForSelection,
  getImageKey,
  leftBoxConveyorStyle,
  rightBoxConveyorStyle,
  motorImageUrls,
  taImageUrls,
  stkImageUrls,
  onMotorSelect,
  motorVariantByBox,
  onDeletePortion,
  sampleData,
  setSampleData,
  isGeneratingPdf,
  handleSTKChange,
  stkVariantByPortion,
  handleTAChange,
  taVariantByPortion
}) => {
  const scrollContainerRef = React.useRef(null);
  const { collapsed, isHovered } = useSidebar()

  // Refs for tracking
  const prevBoxItemsRef = useRef(new Map());
  const prevCheckedItemsRef = useRef(new Map());
  // loadingImagesRef is a React ref that holds a Set of image keys that are currently being loaded.
  // It acts as a tracking mechanism to prevent multiple simultaneous loading attempts for the same image.
  const loadingImagesRef = useRef(new Set());
  const imageCacheRef = useRef(new Map()); // Local cache for this component

  // State for ISO modal
  const [isoModalOpen, setIsoModalOpen] = useState(false);
  const [selectedImageFilename, setSelectedImageFilename] = useState('');

  // Handle wheel events for smooth horizontal scrolling
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // Function to get the regular image filename (for ISO modal)
  const getRegularImageFilename = useCallback((item, portionIndex) => {
    if (item.alt.startsWith('SP-')) return 'SPACER.jpg';
    
    const selectedLabels = item.labels.filter(label => { 
      const key = `${portionIndex}-${label}`; 
      return checkedItems.get(key) === true;
    });
    return generateConveyorFilename(
      selectedLabels, 
      item.alt, 
      boxName === 'left' ? leftBoxConveyorStyle : rightBoxConveyorStyle, 
      boxName
    );
  }, [checkedItems, generateConveyorFilename, boxName, leftBoxConveyorStyle, rightBoxConveyorStyle]);

  // Handle right-click on image to open ISO modal
  const handleImageRightClick = useCallback((e, item, portionIndex) => {
    e.preventDefault();
    
    if (item?.type === 'option' && !item.alt.startsWith('SP-')) {
      const filename = getRegularImageFilename(item, portionIndex);
      setSelectedImageFilename(filename);
      setIsoModalOpen(true);
    }
  }, [getRegularImageFilename]);

  // Helper function to check if checked items for a portion have changed
  const checkIfCheckedItemsChanged = useCallback((portionIndex, item) => {
    if (!item?.labels) return false;
    
    // Check if any checkbox for this portion has changed
    const changed = item.labels.some(label => {
      const key = `${portionIndex}-${label}`;
      const wasChecked = prevCheckedItemsRef.current.get(key);
      const isChecked = checkedItems.get(key);
      const hasChanged = wasChecked !== isChecked;
      
      if (hasChanged) { console.log(`🔄 Checkbox changed for ${key}: was ${wasChecked}, now ${isChecked}`); }
      
      return hasChanged;
    });
    
    if (changed) { console.log(`🔄 Checkbox state changed for portion ${portionIndex}, item ${item.alt}`); }
    
    return changed;
  }, [checkedItems]);

// Smart image loading: Only load the latest changed image
useEffect(() => {

  const loadImages = async () => {
    if (Object.keys(boxItems).length === 0) return;

    const currentBoxItems = new Map(Object.entries(boxItems));
    const previousBoxItems = prevBoxItemsRef.current;
    
    // Find all items that need loading
    const itemsToLoad = [];
    
    // Create a Set to track unique image keys in this batch
    const uniqueImageKeys = new Set();
    
    for (const [portionIndex, item] of currentBoxItems.entries()) {
      if (item?.type !== 'option') continue;
      
      const imageKey = getImageKey(item, portionIndex, boxName, checkedItems);
      const previousItem = previousBoxItems.get(portionIndex);
      
      // Skip if we've already processed this image key in this batch
      if (uniqueImageKeys.has(imageKey)) {
        continue;
      }
      
      // Check if item has actually changed
      const hasItemChanged = !previousItem || JSON.stringify(item) !== JSON.stringify(previousItem);
      
      // Check if checked items for this portion have changed
      const hasCheckedItemsChanged = checkIfCheckedItemsChanged(portionIndex, item);
      
      // Load if item is new, changed, checked items changed, or not already loaded
      const shouldLoad = hasItemChanged || hasCheckedItemsChanged || (!globalImageCache.has(imageKey) && !imageCacheRef.current.has(imageKey));

      if (shouldLoad) {
        // Add to tracking set to prevent duplicates in this batch
        uniqueImageKeys.add(imageKey);
        itemsToLoad.push({ item, portionIndex, imageKey });
      } else {
        console.log(`⏭️ Skipping image load for key: ${imageKey}`);
      }
    }
    
    // Update the refs for next comparison
    prevBoxItemsRef.current = currentBoxItems;
    if (itemsToLoad.length === 0) return;
    // Load all items that need updating
    let handledOne = false;

    const loadPromises = itemsToLoad.map(async ({ item, portionIndex, imageKey }) => {
      if (loadingImagesRef.current.has(imageKey)) {
        console.log(`⏭️ Image already loading: ${imageKey}`);
        return;
      }
    
      loadingImagesRef.current.add(imageKey);
    
      try {
        const url = await getImageForSelection(item, portionIndex, boxName, checkedItems);
        if (url) {
          console.log('✅ Successfully loaded image for:', imageKey);
        } else {
          if (handledOne) return; // skip if already handled one this round
          handledOne = true;
          const cleanImageKey = imageKey.split("-").slice(1).join("-");
    
          setSampleData(prevData => {
            const normalize = str => (str || "").replace(/N/g, "");
            const index = prevData.findIndex(
              item => normalize(item.imageName) === normalize(cleanImageKey)
            );
    
            if (index === -1) {
              console.log("❌ imageKey not found in sampleData:", cleanImageKey);
              return prevData;
            }
    
            const updatedData = [...prevData];
            const currentItem = updatedData[index];
    
            if (currentItem.qty > 1) {
              updatedData[index] = { ...currentItem, qty: currentItem.qty - 1, };
            } else {
              updatedData.splice(index, 1);
            }
    
            return updatedData;
          });
        }
      } catch (error) {
        console.error('Error loading image:', error);
      } finally {
        loadingImagesRef.current.delete(imageKey);
      }
    });

    await Promise.all(loadPromises);

    // After processing loads, record the latest checked state for next diff
    prevCheckedItemsRef.current = new Map(checkedItems);
  };

  loadImages();
}, [boxItems, getImageKey, checkIfCheckedItemsChanged, boxName, checkedItems]);

  // Cleanup effect to prevent memory leaks - Store the current ref value in a variable
  useEffect(() => {
    const currentLoadingImagesRef = loadingImagesRef.current;
    return () => { currentLoadingImagesRef.clear(); };
  }, []);

  // Clear local cache when triggered from parent
  useEffect(() => {
    if (cacheClearTrigger > 0) {
      imageCacheRef.current.clear();
      loadingImagesRef.current.clear();
      prevBoxItemsRef.current.clear();
      prevCheckedItemsRef.current.clear();
    }
  }, [cacheClearTrigger, boxName]);

  // Determine which labels to show based on conveyor style and item type.
  // MT is available on every conveyor (any position), and infeed/outfeed
  // expose the same set of options.
  const getVisibleLabels = useCallback((item) => {
    if (!conveyorStyle || !item?.labels) return [];

    const styleValue = typeof conveyorStyle === 'string'
      ? conveyorStyle
      : conveyorStyle.value || '';

    const styleUpper = styleValue.toUpperCase();

    // Don't Show all labels for Lift Table (LTOPTION) or Angled Table (ATBL)
    if (item.alt === 'LFT' || item.alt === 'LTOPTION' || item.alt === 'ATBL') {
      return [];
    }

    // Handle different conveyor styles
    if (styleUpper.includes('EX') || styleUpper.includes('EXTREME')) {
      // For EX style, show only 'MT' label if it exists
      return item.labels.includes('MT') ? ['MT'] : [];
    } else if (styleUpper.includes('SG') || styleUpper.includes('SIGMA')) {
      // For SG style, show no labels
      return [];
    } else {
      // For all other styles (HD, etc.), show all labels for every position
      return item.labels;
    }
  }, [conveyorStyle]);

  // Precompute TA/STK selections for each portion
  const taSelectionLookup = useMemo(() => {
    const lookup = {};
    if (taSelections[boxName]) {
      Object.keys(taSelections[boxName]).forEach(index => { lookup[index] = true; });
    }
    return lookup;
  }, [taSelections, boxName]);

  const stkSelectionLookup = useMemo(() => {
    const lookup = {};
    if (stkSelections[boxName]) {
      Object.keys(stkSelections[boxName]).forEach(index => { lookup[index] = true; });
    }
    return lookup;
  }, [stkSelections, boxName]);

  const handleBoxClick = useCallback(() => {
    setIsRotatedBox(boxName === 'right');
    setActiveBox(boxName);
  }, [boxName, setIsRotatedBox, setActiveBox]);

  // Memoize the box items rendering
  const renderedBoxItems = useMemo(() => {
    return Object.keys(boxItems).map((portionIndex) => {
      const item = boxItems[portionIndex];
      const imageKey = getImageKey(item, portionIndex, boxName, checkedItems);
      
      // Check global cache first, then local cache
      const currentImage = globalImageCache.get(imageKey) || imageCacheRef.current.get(imageKey);
      const isLoading = loadingImagesRef.current.has(imageKey);

      const visibleLabels = getVisibleLabels(item, portionIndex);
      
      // Generate box label based on box type
      const boxLabel = boxName === 'left' 
        ? `IF # ${parseInt(portionIndex) + 1}`
        : `OF # ${parseInt(portionIndex) + 1}`;
      
      return (
        <BoxItem
          key={`${portionIndex}-${imageKey}-${currentImage ? 'loaded' : 'loading'}`}
          item={item}
          portionIndex={portionIndex}
          boxName={boxName}
          isFlippedVertically={isFlippedVertically}
          visibleLabels={visibleLabels}
          checkedItems={checkedItems}
          handleCheckboxChange={handleCheckboxChange}
          imageUrl={currentImage}
          isLoading={isLoading}
          onImageRightClick={handleImageRightClick}
          hasMotor={motorSelections[boxName] === portionIndex}
          motorImageUrls={motorImageUrls}
          hasTA={taSelectionLookup[portionIndex]}
          taImageUrls={taImageUrls}
          hasSTK={stkSelectionLookup[portionIndex]}
          stkImageUrls={stkImageUrls}
          isFlipped={filenameFlipStates[boxName]}
          onMotorSelect={onMotorSelect}
          motorVariantByBox={motorVariantByBox}
          onDeletePortion={onDeletePortion}
          collapsed={collapsed}
          isHovered={isHovered}
          boxLabel={boxLabel}
          conveyorStyle={conveyorStyle}
          isGeneratingPdf={isGeneratingPdf}
          handleSTKChange={handleSTKChange}
          stkVariantByPortion={stkVariantByPortion}
          handleTAChange={handleTAChange}
          taVariantByPortion={taVariantByPortion}
          motorSelections={motorSelections}
        />
      );
    });
  }, [
    boxItems,
    boxName,
    isFlippedVertically,
    checkedItems,
    handleCheckboxChange,
    globalImageCache,
    getVisibleLabels,
    handleImageRightClick,
    motorSelections,
    motorImageUrls,
    taSelectionLookup,
    taImageUrls,
    stkSelectionLookup,
    stkImageUrls,
    filenameFlipStates,
    getImageKey,
    onMotorSelect,
    motorVariantByBox,
    collapsed,
    isHovered,
    onDeletePortion,
    conveyorStyle,
    isGeneratingPdf,
    handleSTKChange,
    stkVariantByPortion,
    handleTAChange,
    taVariantByPortion
  ]);

  return (
    <>
      <div 
        className={`p-0 text-sm rounded-lg overflow-hidden transition-all duration-300
          ${boxName === 'left'
            ? !isRotatedBox ? 'bg-yellow-400' : 'bg-gray-300'
            : isRotatedBox ? 'bg-yellow-400' : 'bg-gray-300'
          }
          ${ (boxName === 'left' && isRotatedBox) || (boxName === 'right' && !isRotatedBox) 
            ? 'cursor-pointer' 
            : 'cursor-default'
          } ${collapsed && !isHovered ? 'w-[737px]' : 'w-[592px]'} h-[500px]`}
        onClick={handleBoxClick}
      >
        <div 
          ref={scrollContainerRef}
          className={`flex h-full ${
            boxName === 'left' ? 'flex-row-reverse' : 'flex-row'
          } overflow-x-auto scrollbar-thin scrollbar-track-gray-200 snap-x snap-mandatory custom-scrollbar gap-2 px-2`}
        >
          {renderedBoxItems}
        </div>
      </div>
      
      <IsoModal isOpen={isoModalOpen} onClose={() => setIsoModalOpen(false)} imageFilename={selectedImageFilename} boxName={boxName} />
    </>
  );
};

BoxComponent.propTypes = {
  boxItems: PropTypes.object.isRequired,
  boxName: PropTypes.oneOf(['left', 'right']).isRequired,
  isRotatedBox: PropTypes.bool.isRequired,
  setIsRotatedBox: PropTypes.func.isRequired,
  checkedItems: PropTypes.object,
  handleCheckboxChange: PropTypes.func.isRequired,
  conveyorStyle: PropTypes.string,
  isReversed: PropTypes.bool,
  generateConveyorFilename: PropTypes.func.isRequired,
  isFlippedVertically: PropTypes.bool,
  onBoxItemsChange: PropTypes.func.isRequired,
  onCheckedItemsChange: PropTypes.func.isRequired,
  setActiveBox: PropTypes.func.isRequired,
  motorSelections: PropTypes.object.isRequired,
  taSelections: PropTypes.object.isRequired,
  stkSelections: PropTypes.object.isRequired,
  filenameFlipStates: PropTypes.object.isRequired,
  globalImageCache: PropTypes.instanceOf(Map).isRequired,
  setGlobalImageCache: PropTypes.func.isRequired,
  cacheClearTrigger: PropTypes.any,
  imageDetails: PropTypes.object,
  imageDisplayBox: PropTypes.string,
  isImageBox: PropTypes.string,
  getImageForSelection: PropTypes.func.isRequired,
  getImageKey: PropTypes.func.isRequired,
  leftBoxConveyorStyle: PropTypes.string,
  rightBoxConveyorStyle: PropTypes.string,
  // Special component image URLs
  motorImageUrls: PropTypes.shape({
    left: PropTypes.shape({
      motor: PropTypes.string,
      motor1: PropTypes.string,
      motor2: PropTypes.string
    }),
    right: PropTypes.shape({
      motor: PropTypes.string,
      motor1: PropTypes.string,
      motor2: PropTypes.string
    })
  }),
  taImageUrls: PropTypes.shape({
    left: PropTypes.shape({
      up: PropTypes.string,
      down: PropTypes.string
    }),
    right: PropTypes.shape({
      up: PropTypes.string,
      down: PropTypes.string
    })
  }),
  stkImageUrls: PropTypes.shape({
    left: PropTypes.shape({
      high: PropTypes.string,
      low: PropTypes.string
    }),
    right: PropTypes.shape({
      high: PropTypes.string,
      low: PropTypes.string
    })
  }),
  onMotorSelect: PropTypes.func,
  motorVariantByBox: PropTypes.object,
  onDeletePortion: PropTypes.func,
  isGeneratingPdf: PropTypes.bool,
  handleSTKChange: PropTypes.func,
  stkVariantByPortion: PropTypes.object,
  handleTAChange: PropTypes.func,
  taVariantByPortion: PropTypes.object
};

export default React.memo(BoxComponent);
